import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  confirmInsight,
  dismissInsight,
  getInsightDetail,
  getInsightUnderstanding,
} from "../../../Services/journey";
import "../style.css";
import { useStandaloneHeader, JourneyBackHeader, JourneySubpageCard } from "../shared";
import "./style.css";

/**
 * Understanding the Pattern (/journey/insight/:insightId — Figma 9453:7220).
 *
 * Primary source is `GET /journey/insight/{id}/detail` (aris-ai
 * `PatternDetail`): what_aris_is_seeing, why_aris_is_seeing_this,
 * supporting_evidence resolved to dated Journey Events, related_areas,
 * related_elements, recommended_next_step (null until AGS recommends one).
 * If detail 404s (older deploys) we fall back to `/understanding`, whose
 * payload nests everything under `sections` with bare evidence refs — both
 * shapes normalize into one view model here and every field renders
 * defensively.
 */

const Chevron = () => <span aria-hidden="true">›</span>;

/** "PERSONAL_GROWTH" → "Personal Growth" (enum values arrive raw). */
const prettify = (value) =>
  String(value || "")
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(" ");

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString([], {
      month: "short",
      day: "numeric",
    });
  } catch (_) {
    return "";
  }
};

/** Fallback tile glyphs when the event carries no icon of its own. */
const EVENT_ICON = {
  DAILY_CHECK_IN: "✅",
  WEEKLY_REFLECTION: "📝",
  MONTHLY_REFLECTION: "📝",
  JOURNAL_ENTRY: "✍️",
  APPLIED_IN_LIFE: "⚙️",
  INSIGHT_CONFIRMED: "✦",
  VOICE_CONVERSATION: "🎙️",
};

/** Normalize either payload shape (detail vs understanding) into one view model. */
const normalizeInsight = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  if (raw.sections && typeof raw.sections === "object") {
    const sections = raw.sections;
    return {
      what: sections.what_aris_is_seeing?.body || "",
      why: sections.why_aris_is_seeing_this?.body || "",
      evidence: (sections.supporting_evidence?.evidence_refs || []).map(
        (ref) => ({ ref, resolved: false })
      ),
      relatedAreas: sections.related_areas?.life_areas || [],
      relatedElements: sections.related_elements?.elements || [],
      nextStep:
        raw.recommended_next_step ||
        sections.recommended_next_step?.body ||
        null,
    };
  }
  return {
    what: raw.what_aris_is_seeing || raw.text || raw.description || "",
    why: raw.why_aris_is_seeing_this || raw.why || "",
    evidence: raw.supporting_evidence || raw.evidence || [],
    relatedAreas: raw.related_areas || raw.related_life_areas || [],
    relatedElements: raw.related_elements || [],
    nextStep: raw.recommended_next_step || raw.next_step || null,
  };
};

const WhySection = ({ why, evidence }) => {
  const resolved = (evidence || []).filter(
    (item) => item?.resolved !== false && (item?.title || item?.summary)
  );
  if (!why && resolved.length === 0) return null;
  return (
    <section className="journey-card">
      <p
        className="journey-card__eyebrow"
        style={{ color: "var(--journey-muted)" }}
      >
        WHY ARIS IS SEEING THIS
      </p>
      {why && <p className="journey-insight__why">{why}</p>}
      {resolved.length > 0 && (
        <div className="journey-insight__grid">
          {resolved.slice(0, 4).map((item, index) => (
            <div className="journey-insight__cell" key={item.ref || index}>
              <span aria-hidden="true">
                {item.icon || EVENT_ICON[item.event_type] || "✦"}
              </span>
              <p className="journey-insight__cell-text">
                {item.title || item.summary}
              </p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const EvidenceList = ({ evidence }) => {
  if (!Array.isArray(evidence) || evidence.length === 0) return null;
  return (
    <section>
      <p
        className="journey-card__eyebrow"
        style={{ color: "var(--journey-muted)", marginBottom: 12 }}
      >
        SUPPORTING EVIDENCE
      </p>
      <div className="journey-list">
        {evidence.map((item, index) => (
          <div className="journey-list__row" key={item.ref || index}>
            <span className="journey-insight__date">
              {item.resolved !== false ? formatDate(item.occurred_at) : "—"}
            </span>
            <span
              className="journey-list__tile"
              style={{ background: "var(--journey-blue-bg)" }}
            >
              <span role="img" aria-label="">
                {item.icon || EVENT_ICON[item.event_type] || "✦"}
              </span>
            </span>
            <div className="journey-list__text">
              {item.resolved === false ? (
                <p className="journey-list__sub">
                  This moment is no longer available.
                </p>
              ) : (
                <>
                  <p className="journey-list__label">
                    {item.title || prettify(item.event_type)}
                  </p>
                  {item.summary && (
                    <p className="journey-list__sub">{item.summary}</p>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const RelatedSection = ({ areas, elements }) => {
  const hasAreas = Array.isArray(areas) && areas.length > 0;
  const hasElements = Array.isArray(elements) && elements.length > 0;
  if (!hasAreas && !hasElements) return null;
  return (
    <section className="journey-card">
      {hasAreas && (
        <>
          <p
            className="journey-card__eyebrow"
            style={{ color: "var(--journey-muted)" }}
          >
            RELATED AREAS
          </p>
          <div className="journey-insight__chips">
            {areas.map((area) => (
              <span className="journey-insight__chip" key={area}>
                {prettify(area)}
              </span>
            ))}
          </div>
        </>
      )}
      {hasElements && (
        <>
          <p
            className="journey-card__eyebrow"
            style={{
              color: "var(--journey-muted)",
              marginTop: hasAreas ? 16 : 0,
            }}
          >
            RELATED ELEMENTS
          </p>
          <div className="journey-insight__chips">
            {elements.map((element, index) => (
              <span
                className="journey-insight__chip journey-insight__chip--element"
                key={element?.name || index}
              >
                {element?.name || String(element)}
              </span>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

const NextStepCard = ({ nextStep, onChat }) => (
  <section className="journey-card">
    <p
      className="journey-card__eyebrow"
      style={{ color: "var(--journey-link)" }}
    >
      RECOMMENDED NEXT STEP
    </p>
    <p className="journey-insight__next-step">
      {nextStep ||
        "Aris will suggest a next step here as this pattern develops. In the meantime, you can talk it through together."}
    </p>
    <button type="button" className="journey-button" onClick={onChat}>
      Take the Next Step <span aria-hidden="true">→</span>
    </button>
  </section>
);

function InsightScreen() {
  const navigate = useNavigate();
  const { insightId } = useParams();
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [feedback, setFeedback] = useState("idle"); // idle | saving | failed

  useStandaloneHeader();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let payload;
        try {
          payload = await getInsightDetail(insightId);
        } catch (err) {
          // Older deploys have only /understanding — fall back on 404.
          if (err?.response?.status !== 404) throw err;
          payload = await getInsightUnderstanding(insightId);
        }
        if (!cancelled) setInsight(normalizeInsight(payload));
      } catch (_) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [insightId]);

  const sendFeedback = async (action) => {
    if (feedback === "saving") return;
    setFeedback("saving");
    try {
      await action(insightId);
      navigate("/journey");
    } catch (_) {
      setFeedback("failed");
    }
  };

  return (
    <div className="journey-page">
      <div className="journey-page__inner">
        <JourneyBackHeader to="/journey" title="Understanding the Pattern" />
        <JourneySubpageCard>

          {loading && (
            <div className="journey-card journey-card--status">
              Loading this pattern…
            </div>
          )}
          {!loading && (error || !insight) && (
            <div className="journey-card journey-card--status">
              We couldn't load this pattern just now. Try again in a moment.
            </div>
          )}

          {!loading && !error && insight && (
            <>
              <section className="journey-card journey-hero journey-hero--insight">
                <p className="journey-hero__eyebrow">✧ WHAT ARIS IS SEEING</p>
                <p className="journey-hero__body" style={{ marginBottom: 0 }}>
                  {insight.what}
                </p>
                <span className="journey-hero__orb" />
              </section>

              <WhySection why={insight.why} evidence={insight.evidence} />
              <EvidenceList evidence={insight.evidence} />
              <RelatedSection
                areas={insight.relatedAreas}
                elements={insight.relatedElements}
              />
              <NextStepCard
                nextStep={insight.nextStep}
                onChat={() => navigate("/chat")}
              />

              <section className="journey-insight__actions">
                <p className="journey-card__meta">Does this feel accurate?</p>
                <div className="journey-insight__actions-row">
                  <button
                    type="button"
                    className="journey-link"
                    disabled={feedback === "saving"}
                    onClick={() => sendFeedback(confirmInsight)}
                  >
                    This feels right <Chevron />
                  </button>
                  <button
                    type="button"
                    className="journey-link journey-insight__dismiss"
                    disabled={feedback === "saving"}
                    onClick={() => sendFeedback(dismissInsight)}
                  >
                    Not quite
                  </button>
                </div>
                {feedback === "failed" && (
                  <p className="journey-card__note">
                    That didn't save — please try again.
                  </p>
                )}
              </section>
            </>
          )}
        </JourneySubpageCard>
      </div>
    </div>
  );
}

export default InsightScreen;
